import { useState, useEffect, useCallback } from 'react';
import { ref, push, set, onValue, remove, update } from 'firebase/database';
import { ref as storageRef, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { database, storage } from '../firebase/config';

// Free tier limits
const FREE_SONG_LIMIT = 5;
const FREE_VERSION_LIMIT = 2;

export default function useSongs({ user, bandId, isPremium }) {
  const [songs, setSongs] = useState([]);
  const [bandMembers, setBandMembers] = useState([]);
  const [ideas, setIdeas] = useState([]);
  const [userSongOrder, setUserSongOrder] = useState(null);
  const [firebaseReady, setFirebaseReady] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [uploadProgress, setUploadProgress] = useState({});
  const [addingFromIdea, setAddingFromIdea] = useState(false);
  const [commenterName, setCommenterName] = useState('');

  // Set commenter name from user
  useEffect(() => {
    if (user) {
      setCommenterName(user.displayName || user.email.split('@')[0]);
    }
  }, [user]);

  // Fetch band members
  useEffect(() => {
    if (!user || !bandId) {
      setBandMembers([]);
      return;
    }

    const bandRef = ref(database, `bands/${bandId}/members`);
    const unsubscribe = onValue(bandRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const membersArray = Object.keys(data).map(uid => ({ uid, ...data[uid] }));
        setBandMembers(membersArray);
      } else {
        setBandMembers([]);
      }
    });

    return () => unsubscribe();
  }, [user, bandId]);

  // Listen to songs data
  useEffect(() => {
    if (!user || !bandId) {
      setSongs([]);
      setFirebaseReady(false);
      return;
    }

    const songsRef = ref(database, `bands/${bandId}/songs`);

    const timeout = setTimeout(() => {
      setFirebaseReady(true);
      setError('Connection timeout. Try refreshing the page.');
    }, 5000);

    const unsubscribe = onValue(songsRef, (snapshot) => {
      clearTimeout(timeout);
      const data = snapshot.val();
      if (data) {
        const songsArray = Object.keys(data).map(key => ({
          id: key,
          ...data[key]
        }));
        setSongs(songsArray);
      } else {
        setSongs([]);
      }
      setFirebaseReady(true);
    }, (err) => {
      clearTimeout(timeout);
      setError('Failed to connect to Firebase: ' + err.message);
      setFirebaseReady(true);
    });

    return () => {
      clearTimeout(timeout);
      unsubscribe();
    };
  }, [user, bandId]);

  // Fetch user's custom song order
  useEffect(() => {
    if (!user || !bandId) {
      setUserSongOrder(null);
      return;
    }

    const orderRef = ref(database, `users/${user.uid}/songOrder/${bandId}`);
    const unsubscribe = onValue(orderRef, (snapshot) => {
      setUserSongOrder(snapshot.val() || null);
    });

    return () => unsubscribe();
  }, [user, bandId]);

  // Fetch ideas for "Add from Ideas" feature
  useEffect(() => {
    if (!user || !bandId) {
      setIdeas([]);
      return;
    }

    const ideasRef = ref(database, `bands/${bandId}/ideas`);

    const unsubscribe = onValue(ideasRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const ideasArray = Object.keys(data)
          .map(key => ({ id: key, ...data[key] }))
          .filter(idea => idea.status !== 'converted')
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setIdeas(ideasArray);
      } else {
        setIdeas([]);
      }
    });

    return () => unsubscribe();
  }, [user, bandId]);

  // Get sorted songs
  const getSortedSongs = useCallback(() => {
    if (!songs.length) return [];

    if (userSongOrder && Array.isArray(userSongOrder)) {
      const orderMap = {};
      userSongOrder.forEach((id, idx) => orderMap[id] = idx);

      return [...songs].sort((a, b) => {
        const iA = orderMap[a.id];
        const iB = orderMap[b.id];
        if (iA !== undefined && iB !== undefined) return iA - iB;
        if (iA === undefined) return -1;
        if (iB === undefined) return 1;
        return new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt);
      });
    }

    return [...songs].sort((a, b) =>
      new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt)
    );
  }, [songs, userSongOrder]);

  // Check limits
  const canAddSong = isPremium || songs.length < FREE_SONG_LIMIT;

  const canAddVersion = (song) => {
    if (isPremium) return true;
    const versionCount = song.versions ? Object.keys(song.versions).length : 0;
    return versionCount < FREE_VERSION_LIMIT;
  };

  const getVersionCount = (song) => {
    return song.versions ? Object.keys(song.versions).length : 0;
  };

  // File upload helper
  const handleFileUpload = async (file, path, metadata) => {
    if (!file || !user || !bandId) return null;

    try {
      const timestamp = Date.now();
      const filename = `bands/${bandId}/${path}/${timestamp}_${file.name}`;
      const fileRef = storageRef(storage, filename);

      const uploadTask = uploadBytesResumable(fileRef, file);

      return new Promise((resolve, reject) => {
        uploadTask.on('state_changed',
          (snapshot) => {
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            setUploadProgress(prev => ({ ...prev, [path]: progress }));
          },
          (err) => {
            setError('Upload failed: ' + err.message);
            reject(err);
          },
          async () => {
            try {
              const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
              setUploadProgress(prev => ({ ...prev, [path]: 0 }));
              resolve({
                fileName: file.name,
                fileURL: downloadURL,
                storagePath: filename,
                uploadDate: new Date().toISOString(),
                fileSize: (file.size / 1024).toFixed(2),
                uploadedBy: user.uid,
                uploadedByName: user.displayName || user.email,
                ...metadata
              });
            } catch (urlError) {
              reject(urlError);
            }
          }
        );
      });
    } catch (err) {
      setError('Failed to upload file: ' + err.message);
      return null;
    }
  };

  const deleteFile = async (storagePath) => {
    if (storagePath) {
      const fileRef = storageRef(storage, storagePath);
      await deleteObject(fileRef);
    }
  };

  // CRUD Operations
  const addSong = async (songName, songTempo, songLyrics, songAudio) => {
    if (!canAddSong) {
      setError(`Free accounts are limited to ${FREE_SONG_LIMIT} songs. Upgrade to Premium for unlimited songs.`);
      return false;
    }
    if (!songName.trim() || !user || !bandId) return false;

    try {
      setLoading(true);
      const songsRef = ref(database, `bands/${bandId}/songs`);
      const newSongRef = push(songsRef);
      const songId = newSongRef.key;

      const songData = {
        name: songName,
        tempo: songTempo?.trim() || null,
        versions: {},
        lyrics: null,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        createdBy: user.uid,
        createdByName: user.displayName || user.email
      };

      if (songLyrics) {
        const lyricsData = await handleFileUpload(songLyrics, `lyrics/${songId}`, {});
        if (lyricsData) {
          songData.lyrics = lyricsData;
        }
      }

      await set(newSongRef, songData);

      if (songAudio) {
        const audioData = await handleFileUpload(songAudio, `audio/${songId}`, {});
        if (audioData) {
          const versionRef = ref(database, `bands/${bandId}/songs/${songId}/versions`);
          const newVersionRef = push(versionRef);
          await set(newVersionRef, {
            ...audioData,
            notes: '',
            uploadedBy: user.uid,
            uploadedByName: user.displayName || user.email
          });
        }
      }

      setError('');
      setLoading(false);
      return true;
    } catch (err) {
      setError('Failed to add song: ' + err.message);
      setLoading(false);
      return false;
    }
  };

  const updateSongName = async (songId, newName, newTempo) => {
    if (!newName.trim() || !user || !bandId) return false;
    try {
      const songRef = ref(database, `bands/${bandId}/songs/${songId}`);
      await update(songRef, {
        name: newName,
        tempo: newTempo?.trim() || null,
        updatedAt: new Date().toISOString()
      });
      setError('');
      return true;
    } catch (err) {
      setError('Failed to update song name: ' + err.message);
      return false;
    }
  };

  const deleteSong = async (songId) => {
    if (!user || !bandId) return false;
    try {
      const song = songs.find(s => s.id === songId);

      // Cascade-delete audio versions
      if (song && song.versions) {
        const versionsArray = Object.keys(song.versions).map(key => song.versions[key]);
        for (const version of versionsArray) {
          try { await deleteFile(version.storagePath); }
          catch (e) { console.log('Version file not found:', e); }
        }
      }

      // Cascade-delete lyrics file
      if (song && song.lyrics) {
        try { await deleteFile(song.lyrics.storagePath); }
        catch (e) { console.log('Lyrics file not found:', e); }
      }

      // Cascade-delete song attachments
      if (song && song.attachments) {
        const attachmentsArray = Object.keys(song.attachments).map(key => song.attachments[key]);
        for (const att of attachmentsArray) {
          try { await deleteFile(att.storagePath); }
          catch (e) { console.log('Attachment not found:', e); }
        }
      }

      const songRef = ref(database, `bands/${bandId}/songs/${songId}`);
      await remove(songRef);
      setError('');
      return true;
    } catch (err) {
      setError('Failed to delete song: ' + err.message);
      return false;
    }
  };

  const handleAudioUpload = async (songId, file) => {
    if (!file || !user || !bandId) return false;

    const song = songs.find(s => s.id === songId);
    if (song && !canAddVersion(song)) {
      setError(`Free accounts are limited to ${FREE_VERSION_LIMIT} versions per song. Upgrade to Premium for unlimited versions.`);
      return false;
    }
    setLoading(true);

    const fileData = await handleFileUpload(file, `audio/${songId}`, {});

    if (fileData) {
      const versionRef = ref(database, `bands/${bandId}/songs/${songId}/versions`);
      const newVersionRef = push(versionRef);
      await set(newVersionRef, {
        ...fileData,
        notes: '',
        uploadedBy: user.uid,
        uploadedByName: user.displayName || user.email
      });

      const songRef = ref(database, `bands/${bandId}/songs/${songId}`);
      await update(songRef, { updatedAt: new Date().toISOString() });
    }

    setLoading(false);
    return !!fileData;
  };

  const handleLyricsUpload = async (songId, file) => {
    if (!file || !user || !bandId) return false;
    setLoading(true);
    const fileData = await handleFileUpload(file, `lyrics/${songId}`, {});
    if (fileData) {
      const songRef = ref(database, `bands/${bandId}/songs/${songId}`);
      await update(songRef, {
        lyrics: fileData,
        updatedAt: new Date().toISOString()
      });
    }
    setLoading(false);
    return !!fileData;
  };

  const deleteLyrics = async (songId, storagePath) => {
    if (!user || !bandId) return false;
    try {
      await deleteFile(storagePath);
      const songRef = ref(database, `bands/${bandId}/songs/${songId}`);
      await update(songRef, { lyrics: null, updatedAt: new Date().toISOString() });
      return true;
    } catch (err) {
      setError('Failed to delete lyrics: ' + err.message);
      return false;
    }
  };

  // NEW: lyrics text canvas (markdown)
  const updateLyricsText = async (songId, text) => {
    if (!user || !bandId) return false;
    try {
      const songRef = ref(database, `bands/${bandId}/songs/${songId}`);
      const now = new Date().toISOString();
      await update(songRef, {
        lyricsText: text || null,
        lyricsTextUpdatedAt: now,
        lyricsTextUpdatedBy: user.uid,
        lyricsTextUpdatedByName: user.displayName || user.email,
        updatedAt: now,
      });
      setError('');
      return true;
    } catch (err) {
      setError('Failed to save lyrics: ' + err.message);
      return false;
    }
  };

  // NEW: upload a generic attachment to a song
  const uploadSongFile = async (songId, file, title) => {
    if (!file || !user || !bandId) return false;
    setLoading(true);
    const fileData = await handleFileUpload(file, `songAttachments/${songId}`, {});
    if (fileData) {
      const attachmentsRef = ref(database, `bands/${bandId}/songs/${songId}/attachments`);
      const newAttRef = push(attachmentsRef);
      await set(newAttRef, { ...fileData, title: title || file.name });

      const songRef = ref(database, `bands/${bandId}/songs/${songId}`);
      await update(songRef, { updatedAt: new Date().toISOString() });
    }
    setLoading(false);
    return !!fileData;
  };

  // NEW: delete a song attachment
  const deleteSongFile = async (songId, attachmentId, storagePath) => {
    if (!user || !bandId) return false;
    try {
      if (storagePath) {
        try { await deleteFile(storagePath); }
        catch (e) { console.log('Attachment file not found in storage, continuing:', e); }
      }
      const attRef = ref(database, `bands/${bandId}/songs/${songId}/attachments/${attachmentId}`);
      await remove(attRef);

      const songRef = ref(database, `bands/${bandId}/songs/${songId}`);
      await update(songRef, { updatedAt: new Date().toISOString() });
      setError('');
      return true;
    } catch (err) {
      setError('Failed to delete attachment: ' + err.message);
      return false;
    }
  };

  const deleteVersion = async (songId, versionId, storagePath) => {
    if (!user || !bandId) return false;
    try {
      if (storagePath) {
        try {
          await deleteFile(storagePath);
        } catch (err) {
          console.log('File not found in storage, continuing with database deletion');
        }
      }
      const versionRef = ref(database, `bands/${bandId}/songs/${songId}/versions/${versionId}`);
      await remove(versionRef);

      const songRef = ref(database, `bands/${bandId}/songs/${songId}`);
      await update(songRef, { updatedAt: new Date().toISOString() });

      setError('');
      return true;
    } catch (err) {
      setError('Failed to delete version: ' + err.message);
      return false;
    }
  };

  const updateVersionNotes = async (songId, versionId, notes) => {
    if (!user || !bandId) return false;
    try {
      const versionRef = ref(database, `bands/${bandId}/songs/${songId}/versions/${versionId}`);
      await update(versionRef, { notes });

      const songRef = ref(database, `bands/${bandId}/songs/${songId}`);
      await update(songRef, { updatedAt: new Date().toISOString() });

      setError('');
      return true;
    } catch (err) {
      setError('Failed to update notes: ' + err.message);
      return false;
    }
  };

  const addComment = async (songId, versionId, commentText) => {
    if (!commentText.trim() || !commenterName.trim() || !user || !bandId) {
      setError('Please enter your name and a comment');
      return false;
    }

    try {
      const commentsRef = ref(database, `bands/${bandId}/songs/${songId}/versions/${versionId}/comments`);
      const newCommentRef = push(commentsRef);

      const unreadBy = {};
      bandMembers.forEach(member => {
        if (member.uid !== user.uid) {
          unreadBy[member.uid] = true;
        }
      });

      await set(newCommentRef, {
        author: commenterName,
        text: commentText,
        timestamp: new Date().toISOString(),
        userId: user.uid,
        unreadBy: unreadBy
      });

      const songRef = ref(database, `bands/${bandId}/songs/${songId}`);
      await update(songRef, { updatedAt: new Date().toISOString() });

      setError('');
      return true;
    } catch (err) {
      setError('Failed to add comment: ' + err.message);
      return false;
    }
  };

  const deleteComment = async (songId, versionId, commentId) => {
    if (!user || !bandId) return false;
    try {
      const commentRef = ref(database, `bands/${bandId}/songs/${songId}/versions/${versionId}/comments/${commentId}`);
      await remove(commentRef);
      setError('');
      return true;
    } catch (err) {
      setError('Failed to delete comment: ' + err.message);
      return false;
    }
  };

  const addIdeaToSong = async (songId, idea) => {
    if (!user || !bandId) return false;

    const song = songs.find(s => s.id === songId);
    if (song && !canAddVersion(song)) {
      setError(`Free accounts are limited to ${FREE_VERSION_LIMIT} versions per song. Upgrade to Premium for unlimited versions.`);
      return false;
    }

    try {
      setAddingFromIdea(true);

      const response = await fetch(idea.fileURL);
      const blob = await response.blob();

      const timestamp = Date.now();
      const extension = idea.fileName.split('.').pop() || 'webm';
      const fileName = `version-${timestamp}.${extension}`;
      const storagePath = `bands/${bandId}/audio/${songId}/${fileName}`;

      const fileRef = storageRef(storage, storagePath);
      const uploadTask = uploadBytesResumable(fileRef, blob);

      const downloadURL = await new Promise((resolve, reject) => {
        uploadTask.on('state_changed',
          null,
          (err) => reject(err),
          async () => {
            const url = await getDownloadURL(uploadTask.snapshot.ref);
            resolve(url);
          }
        );
      });

      const versionRef = ref(database, `bands/${bandId}/songs/${songId}/versions`);
      const newVersionRef = push(versionRef);

      await set(newVersionRef, {
        fileName,
        fileURL: downloadURL,
        storagePath,
        uploadDate: new Date().toISOString(),
        fileSize: (blob.size / 1024).toFixed(2),
        uploadedBy: user.uid,
        uploadedByName: user.displayName || user.email,
        notes: `From idea: ${idea.label}`
      });

      const songRef = ref(database, `bands/${bandId}/songs/${songId}`);
      await update(songRef, { updatedAt: new Date().toISOString() });

      const ideaRef = ref(database, `bands/${bandId}/ideas/${idea.id}`);
      await update(ideaRef, {
        status: 'converted',
        convertedToSong: songId,
        convertedAt: new Date().toISOString()
      });

      setAddingFromIdea(false);
      setError('');
      return true;
    } catch (err) {
      console.error('Failed to add idea:', err);
      setError('Failed to add idea: ' + err.message);
      setAddingFromIdea(false);
      return false;
    }
  };

  const saveSongOrder = async (newOrder) => {
    if (!user || !bandId) return;
    try {
      const orderRef = ref(database, `users/${user.uid}/songOrder/${bandId}`);
      await set(orderRef, newOrder);
    } catch (err) {
      console.error('Failed to save order:', err);
    }
  };

  const markSongCommentsAsRead = useCallback(async (songId) => {
    if (!user || !bandId) return;

    try {
      const song = songs.find(s => s.id === songId);
      if (!song || !song.versions) return;

      const versionsArray = Object.keys(song.versions);

      for (const versionId of versionsArray) {
        const version = song.versions[versionId];
        if (version.comments) {
          const commentsArray = Object.keys(version.comments);

          for (const commentId of commentsArray) {
            const comment = version.comments[commentId];
            if (comment.unreadBy && comment.unreadBy[user.uid]) {
              const commentRef = ref(database, `bands/${bandId}/songs/${songId}/versions/${versionId}/comments/${commentId}/unreadBy/${user.uid}`);
              await remove(commentRef);
            }
          }
        }
      }
    } catch (err) {
      console.error('Failed to mark comments as read:', err);
    }
  }, [user, bandId, songs]);

  const getUnreadCommentCount = (song) => {
    if (!song.versions || !user) return 0;

    let count = 0;
    const versionsArray = Object.keys(song.versions);

    versionsArray.forEach(versionId => {
      const version = song.versions[versionId];
      if (version.comments) {
        const commentsArray = Object.keys(version.comments);
        commentsArray.forEach(commentId => {
          const comment = version.comments[commentId];
          if (comment.unreadBy && comment.unreadBy[user.uid]) {
            count++;
          }
        });
      }
    });

    return count;
  };

  const getVersionsArray = (versions) => {
    if (!versions) return [];
    return Object.keys(versions).map(key => {
      const version = versions[key];
      const comments = version.comments ?
        Object.keys(version.comments).map(commentKey => ({
          id: commentKey,
          ...version.comments[commentKey]
        })).sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)) :
        [];

      return {
        id: key,
        ...version,
        comments
      };
    }).sort((a, b) => new Date(b.uploadDate) - new Date(a.uploadDate));
  };

  return {
    // State
    songs,
    ideas,
    firebaseReady,
    loading,
    error,
    uploadProgress,
    addingFromIdea,
    commenterName,

    // Computed
    sortedSongs: getSortedSongs(),
    canAddSong,

    // Helpers
    canAddVersion,
    getVersionCount,
    getVersionsArray,
    getUnreadCommentCount,

    // Actions
    setError,
    addSong,
    updateSongName,
    deleteSong,
    handleAudioUpload,
    handleLyricsUpload,
    deleteLyrics,
    updateLyricsText,     // NEW
    uploadSongFile,       // NEW
    deleteSongFile,       // NEW
    deleteVersion,
    updateVersionNotes,
    addComment,
    deleteComment,
    addIdeaToSong,
    saveSongOrder,
    markSongCommentsAsRead,

    // Constants
    FREE_SONG_LIMIT,
    FREE_VERSION_LIMIT,
  };
}
